import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Button,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Stack,
  Menu,
  MenuList,
  ListItemIcon,
  ListItemText,
  Chip,
  Box,
  Typography,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import WorkIcon from '@mui/icons-material/Work';
import GavelIcon from '@mui/icons-material/Gavel';
import PeopleIcon from '@mui/icons-material/People';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AssessmentIcon from '@mui/icons-material/Assessment';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import api from '../services/api';
import { fetchCurrentUser } from '../services/auth';
import { getReportRequests, createReportRequest, approveReportRequest } from '../services/reportRequests';
import { fetchEmployees } from '../services/employee';
import { getEmployeeContracts } from '../services/contract';
import PDFModalReport from '../components/PDFModalReport';
import { logout } from '../utils/auth';

const ReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [reportRequests, setReportRequests] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Request dialog state
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestForm, setRequestForm] = useState({
    report_type: '',
    reason: '',
  });

  // Decision form state
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decisionForm, setDecisionForm] = useState({
    employee_id: '',
    contract_number: '',
    contract_date: new Date().toISOString().split('T')[0],
    decision_start_date: new Date().toISOString().split('T')[0],
    decision_articles: [{ content: '' }],
    decision_type: '',
    effective_date: new Date().toISOString().split('T')[0],
    reason: '',
    charge_execution: ''
  });
  const [employeeContracts, setEmployeeContracts] = useState([]);

  // Employee selection state
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // PDF Modal state
  const [openPDFModal, setOpenPDFModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  // Add report dialog state
  const [addReportOpen, setAddReportOpen] = useState(false);
  const [addReportForm, setAddReportForm] = useState({
    employee_id: '',
    report_type: ''
  });

  // Tab state
  const [activeTab, setActiveTab] = useState(0); // 0 for reports, 1 for requests
  const [requestSubmitting, setRequestSubmitting] = useState(false);

  const reportTypes = [
    { value: 'employee_list', label: 'Liste des employés', icon: <PeopleIcon /> },
    { value: 'attestation_travail', label: 'Attestation de travail', icon: <WorkIcon /> },
    { value: 'certificat_travail', label: 'Certificat de travail', icon: <DescriptionIcon /> },
    { value: 'decision', label: 'Décision', icon: <GavelIcon /> },
  ];

  const statusOptions = [
    { value: 'all', label: 'Tous' },
    { value: 'pending', label: 'En attente' },
    { value: 'approved', label: 'Approuvé' },
    { value: 'rejected', label: 'Rejeté' },
  ];

  const filteredReports = reports.filter((report) => {
    if (filterType !== 'all' && report.type !== filterType) return false;
    if (filterEmployee && !report.employee_name?.toLowerCase().includes(filterEmployee.toLowerCase())) return false;
    return true;
  });

  const filteredRequests = reportRequests.filter((request) => {
    if (filterStatus !== 'all' && request.status !== filterStatus) return false;
    if (filterType !== 'all' && request.report_type !== filterType) return false;
    if (filterEmployee && !request.employee_name?.toLowerCase().includes(filterEmployee.toLowerCase())) return false;
    return true;
  });

  useEffect(() => {
    const loadUser = async () => {
      const user = await fetchCurrentUser();
      if (user) {
        setUserRole(user.role);
        setCurrentUser(user);
      }
      setLoading(false);
    };
    loadUser();
  }, []);

  const loadReports = async () => {
    try {
      const response = await api.get('/reports');
      setReports(response.data || []);
    } catch (error) {
      console.error('Error loading reports:', error);
    }
  };

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const response = await fetchEmployees();
        let allEmployees = response.data || [];
        
        // Filter for admin-tenes: only show TENES site employees
        if (userRole === 5) {
          allEmployees = allEmployees.filter(emp => 
            emp.site?.name?.toLowerCase().includes('tenes') || 
            emp.site_id?.toString().includes('TENES')
          );
        }
        
        setEmployees(allEmployees);
      } catch (error) {
        console.error('Error loading employees:', error);
      }
    };
    loadEmployees();
  }, [userRole]);

  // Load contracts when decision dialog opens and employee is selected
  useEffect(() => {
    if (decisionOpen && decisionForm.employee_id) {
      loadEmployeeContracts(decisionForm.employee_id);
    }
  }, [decisionOpen, decisionForm.employee_id]);

  useEffect(() => {
    loadReports();
  }, [userRole, currentUser]); // Reload reports when user changes

  useEffect(() => {
    const fetchReports = async () => {
      try {
        // Check if user is authenticated
        const token = localStorage.getItem('token');
        if (!token) {
          window.location.href = '/login';
          return;
        }
        
        // Fetch generated reports from new Reports table
        const response = await api.get('/reports');
        setReports(response.data || []);
      } catch (error) {
        console.error('Error loading reports:', error);
        // If authentication error, redirect to login
        if (error.response?.status === 401 || error.response?.status === 403) {
          logout();
          window.location.href = '/login';
        }
        setReports([]);
      }
    };
    fetchReports();
  }, []);

  useEffect(() => {
    const fetchReportRequests = async () => {
      try {
        const response = await getReportRequests();
        setReportRequests(response.data || []);
      } catch (error) {
        console.error('Error loading report requests:', error);
      }
    };
    fetchReportRequests();
  }, []);

  const handleAddReport = async (reportType) => {
    try {
      if (reportType === 'decision') {
        // Open decision form dialog
        setDecisionOpen(true);
      } else if (reportType === 'attestation_travail' || reportType === 'certificat_travail') {
        // Direct generation for attestation and certificat
        if (!selectedEmployee) {
          alert('Veuillez d\'abord sélectionner un employé');
          return;
        }
        
        const endpoint = reportType === 'attestation_travail' ? '/reports/attestation-travail' : '/reports/certificat-travail';
        const payload = { employee_id: selectedEmployee.id };
        
        const response = await api.post(endpoint, payload);
        
        // Get employee data for PDF
        const employeeResponse = await api.get(`/employees/${selectedEmployee.id}`);
        
        // Create report data for PDF modal
        const reportData = {
          id: response.data.id,
          type: reportType,
          type_label: reportType === 'attestation_travail' ? 'Attestation de travail' : 'Certificat de travail',
          employee_name: `${selectedEmployee.first_name} ${selectedEmployee.last_name}`,
          reference: response.data.reference,
          created_at: new Date().toISOString(),
          status: 'approved',
          download_url: response.data.download_url,
          employee: {
            ...employeeResponse.data,
            date_sortie: selectedEmployee.date_sortie || employeeResponse.data.date_sortie
          }
        };
        
        // Add to reports list
        setReports([reportData, ...reports]);
        
        // Show PDF modal instead of downloading
        setSelectedReport(reportData);
        setOpenPDFModal(true);
      }
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Erreur lors de la génération du rapport: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleViewPDF = async (report) => {
    try {
      // Get complete report data from backend
      const reportResponse = await api.get(`/reports/${report.id}`);
      let reportData = reportResponse.data;
      
      // Get employee data if not already present
      if (!reportData.employee && reportData.employee_id) {
        const employeeResponse = await api.get(`/employees/${reportData.employee_id}`);
        reportData.employee = employeeResponse.data;
      }
      
      setSelectedReport(reportData);
      setOpenPDFModal(true);
    } catch (error) {
      console.error('Erreur lors du chargement du PDF:', error);
      alert('Erreur lors du chargement du PDF: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleRequestReport = () => {
    setRequestOpen(true);
  };

  const handleSubmitRequest = async () => {
    if (requestSubmitting) {
      return; // Prevent multiple submissions
    }
    
    setRequestSubmitting(true);
    
    try {
      const newRequest = {
        report_type: requestForm.report_type,
        reason: requestForm.reason,
      };
      
      // Add employee_id for admin-tenes
      if (userRole === 5 && selectedEmployee) {
        newRequest.employee_id = selectedEmployee.id;
      }
      
      const response = await createReportRequest(newRequest);
      setReportRequests([response.data, ...reportRequests]);
      setRequestOpen(false);
      setRequestForm({ report_type: '', reason: '' });
      setSelectedEmployee(null);
    } catch (error) {
      console.error('Error submitting request:', error);
      
      // Handle duplicate error
      if (error.response?.status === 409) {
        alert('Une demande de rapport identique a déjà été créée récemment. Veuillez patienter.');
      } else {
        alert('Erreur lors de la soumission de la demande');
      }
    } finally {
      setRequestSubmitting(false);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce rapport ?')) {
      try {
        await api.delete(`/reports/${reportId}`);
        setReports(reports.filter(r => r.id !== reportId));
      } catch (error) {
        console.error('Error deleting report:', error);
      }
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      await approveReportRequest(requestId);
      setReportRequests(reportRequests.map(req => 
        req.id === requestId ? { ...req, status: 'approved' } : req
      ));
    } catch (error) {
      console.error('Error approving request:', error);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) {
      try {
        await api.delete(`/report-requests/${requestId}`);
        setReportRequests(reportRequests.filter(req => req.id !== requestId));
      } catch (error) {
        console.error('Error deleting request:', error);
      }
    }
  };

  const handleEditDecision = (report) => {
    // Pré-remplir le formulaire de décision avec les données du rapport
    setDecisionForm({
      employee_id: report.employee_id || '',
      contract_number: report.contract_number || '',
      contract_date: report.contract_date || new Date().toISOString().split('T')[0],
      decision_start_date: report.decision_start_date || new Date().toISOString().split('T')[0],
      decision_articles: report.decision_articles && report.decision_articles.length > 0 
        ? report.decision_articles 
        : [{ content: '' }],
      decision_type: report.decision_type || '',
      effective_date: report.effective_date || new Date().toISOString().split('T')[0],
      reason: report.reason || '',
      charge_execution: report.charge_execution || ''
    });
    // Ouvrir le dialogue de décision
    setDecisionOpen(true);
  };

  const handleOpenAddReport = () => {
    setAddReportOpen(true);
    setAddReportForm({
      employee_id: '',
      report_type: ''
    });
  };

  const handleCloseAddReport = () => {
    setAddReportOpen(false);
    setAddReportForm({
      employee_id: '',
      report_type: ''
    });
  };

  const handleAddReportChange = (field) => (event) => {
    setAddReportForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmitAddReport = () => {
    if (!addReportForm.employee_id || !addReportForm.report_type) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    // Fermer la popup d'ajout
    setAddReportOpen(false);

    // Ouvrir le dialogue approprié selon le type de rapport
    if (addReportForm.report_type === 'decision') {
      setDecisionForm({
        employee_id: addReportForm.employee_id,
        contract_number: '',
        contract_date: new Date().toISOString().split('T')[0],
        decision_start_date: new Date().toISOString().split('T')[0],
        decision_articles: [{ content: '' }],
        decision_type: '',
        effective_date: new Date().toISOString().split('T')[0],
        reason: '',
        charge_execution: ''
      });
      setDecisionOpen(true);
    } else {
      // Pour les autres types de rapports, créer directement
      createReportOfType(addReportForm.employee_id, addReportForm.report_type);
    }
  };

  const createReportOfType = async (employeeId, reportType) => {
    try {
      let response;
      
      switch (reportType) {
        case 'attestation_travail':
          response = await api.post('/reports/attestation-travail', { employee_id: employeeId });
          break;
        case 'certificat_travail':
          response = await api.post('/reports/certificat-travail', { employee_id: employeeId });
          break;
        default:
          throw new Error('Type de rapport non supporté');
      }

      // Recharger la liste des rapports
      loadReports();
      
      // Ouvrir le PDF modal
      const reportData = response.data;
      setSelectedReport(reportData);
      setOpenPDFModal(true);
      
    } catch (error) {
      console.error('Error creating report:', error);
      alert('Erreur lors de la création du rapport');
    }
  };

  const handleSubmitDecision = async () => {
    try {
      const response = await api.post('/reports/decision', decisionForm);
      
      // Get employee data for PDF
      const employeeResponse = await api.get(`/employees/${decisionForm.employee_id}`);
      
      // Create report data for PDF modal
      const reportData = {
        id: response.data.id,
        type: 'decision',
        type_label: 'Décision',
        employee_name: employeeResponse.data ? `${employeeResponse.data.first_name} ${employeeResponse.data.last_name}` : '............................',
        reference: response.data.reference,
        created_at: new Date().toISOString(),
        status: 'approved',
        download_url: response.data.download_url,
        employee: employeeResponse.data,
        contract_number: decisionForm.contract_number,
        contract_date: decisionForm.contract_date,
        decision_start_date: decisionForm.decision_start_date,
        decision_articles: decisionForm.decision_articles,
        charge_execution: decisionForm.charge_execution
      };
      
      // Add to reports list
      setReports([reportData, ...reports]);
      
      // Reset form and close dialog
      setDecisionOpen(false);
      setDecisionForm({
        employee_id: '',
        contract_number: '',
        contract_date: new Date().toISOString().split('T')[0],
        decision_start_date: new Date().toISOString().split('T')[0],
        decision_articles: [{ content: '' }],
        charge_execution: ''
      });
      
      // Show PDF modal instead of downloading
      setSelectedReport(reportData);
      setOpenPDFModal(true);
    } catch (error) {
      console.error('Error generating decision:', error);
      alert('Erreur lors de la génération de la décision: ' + (error.response?.data?.error || error.message));
    }
  };

  const addDecisionArticle = () => {
    setDecisionForm({
      ...decisionForm,
      decision_articles: [...decisionForm.decision_articles, { content: '' }]
    });
  };

  const updateDecisionArticle = (index, content) => {
    const updatedArticles = [...decisionForm.decision_articles];
    
    // S'assurer que l'article existe à cet index
    if (!updatedArticles[index]) {
      updatedArticles[index] = {};
    }
    
    updatedArticles[index].content = content;
    setDecisionForm({
      ...decisionForm,
      decision_articles: updatedArticles
    });
  };

  const removeDecisionArticle = (index) => {
    const updatedArticles = decisionForm.decision_articles.filter((_, i) => i !== index);
    setDecisionForm({
      ...decisionForm,
      decision_articles: updatedArticles
    });
  };

  const loadEmployeeContracts = async (employeeId) => {
    try {
      const response = await getEmployeeContracts(employeeId);
      setEmployeeContracts(response.data || []);
    } catch (error) {
      console.error('Error loading employee contracts:', error);
      setEmployeeContracts([]);
    }
  };

  const reportColumns = [
    { field: 'type_label', headerName: 'Type de Rapport', width: 200, headerClassName: 'super-app-theme--header' },
    { field: 'employee_name', headerName: 'Employé', width: 180, headerClassName: 'super-app-theme--header' },
    { field: 'reference', headerName: 'Référence', width: 150, headerClassName: 'super-app-theme--header' },
    { field: 'created_at', headerName: 'Date de création', width: 180, headerClassName: 'super-app-theme--header' },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      sortable: false,
      filterable: false,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Voir PDF">
            <IconButton size="small" onClick={(e) => {
              e.stopPropagation(); // Empêche la propagation du clic
              handleViewPDF(params.row);
            }}>
              <PictureAsPdfIcon sx={{ color: 'green' }} />
            </IconButton>
          </Tooltip>
          {userRole === 1 && (params.row.type_rapport === 'decision' || params.row.type === 'decision') && (
            <Tooltip title="Modifier">
              <IconButton size="small" onClick={(e) => {
                e.stopPropagation(); // Empêche la propagation du clic
                handleEditDecision(params.row);
              }}>
                <EditIcon sx={{ color: 'blue' }} />
              </IconButton>
            </Tooltip>
          )}
          {userRole === 1 && (
            <Tooltip title="Supprimer">
              <IconButton size="small" onClick={(e) => {
                e.stopPropagation(); // Empêche la propagation du clic
                handleDeleteReport(params.row.id);
              }}>
                <DeleteIcon sx={{ color: 'red' }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  const requestColumns = [
    { field: 'employee_name', headerName: 'Employé', width: 180, headerClassName: 'super-app-theme--header' },
    { field: 'report_type_label', headerName: 'Type de Rapport', width: 200, headerClassName: 'super-app-theme--header' },
    { field: 'reason', headerName: 'Motif', flex: 1, minWidth: 200, headerClassName: 'super-app-theme--header' },
    { field: 'created_at', headerName: 'Date de demande', width: 180, headerClassName: 'super-app-theme--header' },
    { field: 'status', headerName: 'Statut', width: 120, headerClassName: 'super-app-theme--header',
      renderCell: (params) => {
        const statusColors = {
          'pending': 'warning',
          'approved': 'success',
          'rejected': 'error'
        };
        const statusLabels = {
          'pending': 'En attente',
          'approved': 'Approuvé',
          'rejected': 'Rejeté'
        };
        return (
          <Chip 
            label={statusLabels[params.value] || params.value}
            color={statusColors[params.value] || 'default'}
            size="small"
          />
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      sortable: false,
      filterable: false,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          {userRole === 1 && params.row.status === 'pending' && (
            <Tooltip title="Approuver">
              <IconButton 
                size="small" 
                onClick={() => handleApproveRequest(params.row.id)}
                sx={{ color: 'green' }}
              >
                <DescriptionIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Supprimer">
            <IconButton 
              size="small" 
              onClick={() => handleDeleteRequest(params.row.id)}
              sx={{ color: 'red' }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <>
      <h2>Documents Administratifs</h2>
      
      {/* Action Buttons */}
      <Stack direction="row" spacing={2} mb={2}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RequestQuoteIcon />}
          onClick={handleRequestReport}
        >
          Demander un rapport
        </Button>
        
        {userRole === 1 && (
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleOpenAddReport}
          >
            Ajouter Rapport
          </Button>
        )}
      </Stack>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Liste des Rapports" />
          <Tab label="Liste des Demandes" />
        </Tabs>
      </Box>

      {/* Filters */}
      <Typography variant="h6" mb={1}>Filtres</Typography>
      <Stack direction="row" spacing={2} mb={2}>
        <Select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          variant="outlined"
          size="small"
          style={{ minWidth: 150 }}
        >
          {statusOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <Select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          variant="outlined"
          size="small"
          style={{ minWidth: 200 }}
        >
          <MenuItem value="all">Tous les types</MenuItem>
          {reportTypes.map(type => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </Select>
        <TextField
          value={filterEmployee}
          onChange={(e) => setFilterEmployee(e.target.value)}
          variant="outlined"
          size="small"
          placeholder="Filtrer par employé..."
          style={{ minWidth: 200 }}
        />
      </Stack>

      {/* Tab Content */}
      {activeTab === 0 && (
        <>
          <Typography variant="h6" mb={1}>Liste des Rapports</Typography>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={filteredReports}
              columns={reportColumns}
              pageSize={20}
              disableSelectionOnClick
              getRowHeight={() => 80}
              sx={{
                '& .super-app-theme--header': {
                  backgroundColor: '#1976d2',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                },
                '& .MuiDataGrid-root': {
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#1976d2',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'white',
                  borderBottom: '2px solid #1565c0',
                },
                '& .MuiDataGrid-columnHeader': {
                  '&:focus': { outline: 'none' },
                  '&:hover': { backgroundColor: '#1565c0' },
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '13px',
                  padding: '16px 8px',
                  borderBottom: '1px solid #f0f0f0',
                  '&:focus': { outline: 'none' },
                },
                '& .MuiDataGrid-row': {
                  '&:nth-of-type(even)': { backgroundColor: '#fafafa' },
                  '&:hover': { backgroundColor: '#e3f2fd' },
                  '&.Mui-selected': { backgroundColor: '#e3f2fd' },
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '1px solid #e0e0e0',
                  backgroundColor: '#f5f5f5',
                },
              }}
            />
          </Box>
        </>
      )}

      {activeTab === 1 && (
        <>
          <Typography variant="h6" mb={1}>Liste des Demandes</Typography>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={filteredRequests}
              columns={requestColumns}
              pageSize={20}
              disableSelectionOnClick
              getRowHeight={() => 80}
              sx={{
                '& .super-app-theme--header': {
                  backgroundColor: '#1976d2',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                },
                '& .MuiDataGrid-root': {
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#1976d2',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'white',
                  borderBottom: '2px solid #1565c0',
                },
                '& .MuiDataGrid-columnHeader': {
                  '&:focus': { outline: 'none' },
                  '&:hover': { backgroundColor: '#1565c0' },
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '13px',
                  padding: '16px 8px',
                  borderBottom: '1px solid #f0f0f0',
                  '&:focus': { outline: 'none' },
                },
                '& .MuiDataGrid-row': {
                  '&:nth-of-type(even)': { backgroundColor: '#fafafa' },
                  '&:hover': { backgroundColor: '#e3f2fd' },
                  '&.Mui-selected': { backgroundColor: '#e3f2fd' },
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '1px solid #e0e0e0',
                  backgroundColor: '#f5f5f5',
                },
              }}
            />
          </Box>
        </>
      )}

      {/* Request Dialog */}
      <Dialog open={requestOpen} onClose={() => setRequestOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Demander un Rapport</DialogTitle>
        <DialogContent>
          {/* Employee selection for admin-tenes */}
          {userRole === 5 && (
            <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
              <InputLabel>Employé</InputLabel>
              <Select
                value={selectedEmployee?.id || ''}
                onChange={(e) => {
                  const employee = employees.find(emp => emp.id === e.target.value);
                  setSelectedEmployee(employee);
                }}
              >
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          <TextField
            margin="dense"
            label="Type de Rapport"
            select
            fullWidth
            value={requestForm.report_type}
            onChange={(e) => setRequestForm({ ...requestForm, report_type: e.target.value })}
            sx={{ mb: 2 }}
          >
            {reportTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="Motif de la demande"
            fullWidth
            multiline
            rows={3}
            value={requestForm.reason}
            onChange={(e) => setRequestForm({ ...requestForm, reason: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestOpen(false)} disabled={requestSubmitting}>Annuler</Button>
          <Button onClick={handleSubmitRequest} variant="contained" disabled={requestSubmitting}>
            {requestSubmitting ? 'Soumission en cours...' : 'Soumettre'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Decision Form Dialog */}
      <Dialog open={decisionOpen} onClose={() => {
        setDecisionOpen(false);
        setEmployeeContracts([]); // Reset contracts when dialog closes
      }} maxWidth="md" fullWidth>
        <DialogTitle>Créer une Décision</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Employé"
            select
            fullWidth
            value={decisionForm.employee_id}
            onChange={async (e) => {
              const employeeId = e.target.value;
              setDecisionForm({ 
                ...decisionForm, 
                employee_id: employeeId,
                contract_number: '',
                contract_date: new Date().toISOString().split('T')[0]
              });
              if (employeeId) {
                await loadEmployeeContracts(employeeId);
              } else {
                setEmployeeContracts([]);
              }
            }}
            sx={{ mb: 2 }}
          >
            {employees.map((employee) => (
              <MenuItem key={employee.id} value={employee.id}>
                {employee.first_name} {employee.last_name}
              </MenuItem>
            ))}
          </TextField>
          
          <TextField
            margin="dense"
            label="N° de contrat"
            select
            fullWidth
            value={decisionForm.contract_number}
            onChange={(e) => {
              const selectedContract = employeeContracts.find(contract => contract.reference === e.target.value);
              setDecisionForm({ 
                ...decisionForm, 
                contract_number: e.target.value,
                contract_date: selectedContract?.created_at?.split('T')[0] || new Date().toISOString().split('T')[0]
              });
            }}
            sx={{ mb: 2 }}
            disabled={!decisionForm.employee_id}
          >
            {employeeContracts.length === 0 ? (
              <MenuItem disabled>
                {decisionForm.employee_id ? 'Aucun contrat trouvé' : 'Sélectionnez d\'abord un employé'}
              </MenuItem>
            ) : (
              employeeContracts.map((contract) => (
                <MenuItem key={contract.id} value={contract.reference}>
                  {contract.reference}
                </MenuItem>
              ))
            )}
          </TextField>
          
          <TextField
            margin="dense"
            label="Date début décision"
            type="date"
            fullWidth
            value={decisionForm.decision_start_date}
            onChange={(e) => setDecisionForm({ ...decisionForm, decision_start_date: e.target.value })}
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
          />
          
          <Typography variant="h6" sx={{ mb: 2 }}>Article de décision</Typography>
          
          <TextField
            label="Article 1"
            fullWidth
            multiline
            rows={2}
            value={decisionForm.decision_articles[0]?.content || ''}
            onChange={(e) => updateDecisionArticle(0, e.target.value)}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Chargé(e) d'éxécution de la décision"
            fullWidth
            multiline
            rows={2}
            value={decisionForm.charge_execution}
            onChange={(e) => setDecisionForm({ ...decisionForm, charge_execution: e.target.value })}
            sx={{ mb: 2 }}
            placeholder="Nom de la personne chargée d'exécuter cette décision"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecisionOpen(false)}>Annuler</Button>
          <Button onClick={handleSubmitDecision} variant="contained">Générer la décision</Button>
        </DialogActions>
      </Dialog>

      {/* PDF Modal */}
      {openPDFModal && selectedReport && (
        <PDFModalReport
          reportData={selectedReport}
          onClose={() => {
            setOpenPDFModal(false);
            setSelectedReport(null);
          }}
        />
      )}

      {/* Add Report Dialog */}
      <Dialog open={addReportOpen} onClose={handleCloseAddReport} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter un rapport</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="employee-select-label">Employé</InputLabel>
              <Select
                labelId="employee-select-label"
                value={addReportForm.employee_id}
                onChange={handleAddReportChange('employee_id')}
                label="Employé"
              >
                <MenuItem value="">Sélectionner un employé</MenuItem>
                {employees.map(employee => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {employee.first_name} {employee.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="report-type-label">Type de rapport</InputLabel>
              <Select
                labelId="report-type-label"
                value={addReportForm.report_type}
                onChange={handleAddReportChange('report_type')}
                label="Type de rapport"
              >
                <MenuItem value="">Sélectionner un type</MenuItem>
                <MenuItem value="attestation_travail">Attestation de travail</MenuItem>
                <MenuItem value="certificat_travail">Certificat de travail</MenuItem>
                <MenuItem value="decision">Décision</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddReport}>Annuler</Button>
          <Button onClick={handleSubmitAddReport} variant="contained">Créer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReportsPage;
